/**
 * Live updates for calendar provider sync: the worker broadcasts
 * `calendar-sync-applied` after a sync run's terminal commit; this hook
 * refetches the calendar views so remote (phone-side) changes appear
 * without waiting for the next manual refresh. Falls back gracefully —
 * when the socket is down, data still lands on the next refetch.
 */

import { useEffect, useRef } from 'react';
import { getWebSocket } from '@/lib/websocket';

/**
 * Same-tab companion to the `calendar-sync-applied` websocket broadcast.
 *
 * Some changes originate in THIS tab and never round-trip through the worker —
 * removing a synced calendar with its imported events, for example. The
 * calendar views are local-state hooks rather than React Query, so there is no
 * cache to invalidate; this reuses the existing refresh path instead of adding
 * a second one.
 */
const CALENDAR_DATA_CHANGED_CHANNEL = 'fias-calendar-data-changed';

export function notifyCalendarDataChanged(): void {
  try {
    const channel = new BroadcastChannel(CALENDAR_DATA_CHANGED_CHANNEL);
    channel.postMessage({ type: 'calendar-data-changed' });
    channel.close();
  } catch {
    // BroadcastChannel unavailable — the next refetch still picks it up.
  }
}

export function useCalendarSyncLive(onApplied: (calendarIds: string[]) => void): void {
  const callbackRef = useRef(onApplied);
  callbackRef.current = onApplied;

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    try {
      const ws = getWebSocket();
      unsubscribe = ws.onCalendarSyncApplied((calendarIds) => {
        callbackRef.current(calendarIds);
      });
    } catch {
      // WebSocket not initialized (e.g. logged-out booking pages) — polling
      // and manual refresh still cover sync updates.
    }
    let channel: BroadcastChannel | undefined;
    try {
      channel = new BroadcastChannel(CALENDAR_DATA_CHANGED_CHANNEL);
      channel.onmessage = () => callbackRef.current([]);
    } catch {
      // Same fallback as above — polling and manual refresh still cover it.
    }

    return () => {
      unsubscribe?.();
      channel?.close();
    };
  }, []);
}
